<template>
	<div class="full-width">
		<AppTable main-color="#6c33cb" ref="logs-table"
			:fetch-function="getFilteredChats"
			:filters="filters"
			:table-headings="tableHeadings"
			:title="getString('chats')"
			:total-items="chatsTotal || 0"
			:items="chats || []"
			initial-sort-field="updated_at"
			:initial-sort-asc="false"
			@failed-fetch="details=>{$emit('failed-fetch', details)}"
		>
			<template #action="{item}">
				<div class="row centered">
					<AppTableButtonGroup :buttons="actionsButtons" :item="item" @show-chat="showChat(item)"/>
				</div>
			</template>
		</AppTable>

		<AppNotification :title="getString('error')" :body-text="getString('generic_error')" :type="-2" ref="generic-error" />
		<AppBottomSheet id="chat-bottomsheet" :undismissable="loadingChat" :force-width="bottomSheetWidth" :active="activeChatPreview" @toggling-popup="togglingChatPreview">
			<ModalHeader :title="bottomSheetTitle" />
			<div class="chat-item-list">
				<AppChatMessages :chat-messages="chatMessages" />
			</div>
		</AppBottomSheet>
	</div>
</template>

<script>
import API from "@/shared/helpers/API";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import AppTable from "@/shared/components/AppTable.vue";
import {useLanguageStore} from "@/shared/stores";
import {getLocalDateFromServer, formatDateYYMMDD} from "@/shared/helpers/dates";
import AppNotification from "@/shared/components/AppNotification.vue";
import AppTableButtonGroup from "@/shared/components/AppTableButtonGroup.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import AppChatMessages from "@/shared/components/AppChatMessages.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";

export default {
	setup() {
		const languageStore = useLanguageStore();
		return {getString: languageStore.getString}
	},
	name: "AIChatsLogs",
	mixins: [WindowWidth],
	components: {AppChatMessages, AppTableButtonGroup, AppNotification, AppButtonIcon, AppBottomSheet, ModalHeader, AppTable},
	data() {
		return {
			loadingChat: false,
			activeChatPreview: false,
			showingChat: null,
			chatMessages: [],
			chats: [],
			chatsTotal: 0,
			actionsButtons: [
				{
					iconName: "icon-eye-blue",
					emitName: "show-chat",
					title: this.getString('show'),
				}
			],
			filters: [
				{
					type: "date",
					key: 'created_at',
					tableName: 'ai_chats',
					text: this.getString('created_date'),
					finalValue: '',
					active: false
				},
				{
					type: "date",
					key: 'updated_at',
					tableName: 'ai_chats',
					text: this.getString('last_message'),
					finalValue: '',
					active: false
				},
				{
					type: "exactMatch",
					key: "business_id",
					text: this.getString('business_id'),
					placeholder: this.getString('business_id'),
					active: false,
					value: null,
					finalValue: '',
				},
			],
			tableHeadings: [
				{
					text: this.getString('created_date'),
					cols: 1,
					sortable: true,
					formatter: 'date',
					fieldName: 'created_at',
				},
				{
					text: this.getString('last_message'),
					cols: 1,
					sortable: true,
					formatter: 'date',
					fieldName: 'updated_at',
					active: true,
					sortAsc: false,
				},
				{
					text: this.getString('business_id'),
					cols: 1,
					fieldName: 'business_id',
				},
				{
					text: this.getString('title'),
					cols: 2,
					fieldName: 'name',
				},
				{
					text: this.getString('action'),
					cols: 1,
					fieldName: 'action',
					slotName: 'action',
				},
			],
		}
	},
	computed: {
		bottomSheetTitle() {
			let finalDate = formatDateYYMMDD(this.showingChat?.created_at || new Date());
			return this.getString('chat_sheet_title', [this.showingChat?.business_id, finalDate, this.showingChat?.name]);
		},
		bottomSheetWidth() {
			if (this.windowWidth < 550) {
				return "100%";
			} else if (this.windowWidth < 768) {
				return "550px";
			} else if (this.windowWidth < 1080) {
				return "720px";
			} else {
				return "960px";
			}
		},
	},
	methods: {
		togglingChatPreview(active) {
			if(!active) {
				this.activeChatPreview = false;
			}
		},
		showChat(chat, callback) {
			this.showingChat = chat;
			this.chatMessages = [];
			this.activeChatPreview = true;
			this.loadingChat = true;
			API.init().superGetChat(chat.ai_chat_id, r=> {
				this.loadingChat = false;
				if(r && r.result === "OK" && r.messages?.length) {
					r.messages.forEach((message) => {
						message = this.normalizeMessage(message);
					})
					this.chatMessages = r.messages;
					if(callback) { callback(true); }
				} else {
					this.$refs["generic-error"].openNotification();
					if(callback) { callback(false, r ? r.details : null); }
				}
			});
		},
		getFilteredChats(filters, callback = null) {

			API.init().superGetChats(JSON.stringify(filters || []), r=> {
				this.chats = [];
				this.chatsTotal = 0;
				if(r && r.result === "OK") {
					if(r.chats && r.chats.selected && r.chats.selected.length) {
						r.chats.selected.forEach(t => {
							this.chats.push(this.normalizeChat(t));
						});
						this.chatsTotal = Number(r.chats.selected_num || 0);
					}
					if(callback) { callback(true); }
				} else {
					this.$refs["generic-error"].openNotification();
					if(callback) { callback(false, r ? r.details : null); }
				}
			});

		},
		normalizeMessage(msg) {
			msg.created_at = msg.created_at ? getLocalDateFromServer(msg.created_at) : null;
			return msg;
		},
		normalizeChat(chat) {
			chat.created_at = chat.created_at ? getLocalDateFromServer(chat.created_at) : null;
			chat.updated_at = chat.updated_at ? getLocalDateFromServer(chat.updated_at) : null;
			chat.business_id = '#'+(chat.business_id || '');
			return chat;
		},
	}
}
</script>

<style lang="scss" scoped>
.full-width {
	width: 100%;
}
.chat-item-list {
	display: flex;
	flex-direction: column;
	gap: 40px;
	height: 100%;
	max-height: calc(100vh - 200px);
	padding: 0 10px;
	overflow-y: scroll;
	scrollbar-width: auto;
	scrollbar-color: var(--color-alpha-1) transparent;

	@media (max-height: 300px) {
		max-height: calc(100vh - 150px);
	}

	@media (max-height: 250px) {
		max-height: 100%;
	}
}
</style>